import React, { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";

// GraphQL
const QUERY_TOTALS = gql`
  query GetMessagesAll {
    getMessagesAll {
      _id
      sender
      recipient
      text
      createdAt
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($sender: String!, $recipient: String!, $text: String!) {
    sendMessage(sender: $sender, recipient: $recipient, text: $text) {
      _id
    }
  }
`;

const CLEAR_MESSAGES = gql`
  mutation ClearMessages($sender: String) {
    clearMessages(sender: $sender)
  }
`;

export default function ChatWithCustomer() {
  const [lastReadMap, setLastReadMap] = useState(() => {
    const stored = localStorage.getItem("lastReadMap");
    return stored ? JSON.parse(stored) : {};
  });
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [text, setText] = useState("");
  const messagesEndRef = useRef(null);

  const { data, loading, error } = useQuery(QUERY_TOTALS, {
    pollInterval: 3000,
  });

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE, {
    refetchQueries: [{ query: QUERY_TOTALS }],
  });

  const [clearMessages, { loading: clearing }] = useMutation(CLEAR_MESSAGES, {
    refetchQueries: [{ query: QUERY_TOTALS }],
  });

  const customerMap = {};
  data?.getMessagesAll?.forEach((msg) => {
    if (msg.sender !== "Admin") {
      if (!customerMap[msg.sender]) {
        customerMap[msg.sender] = [];
      }
      customerMap[msg.sender].push(msg);
    }
  });
  const uniqueCustomers = Object.keys(customerMap);

  const messages = data?.getMessagesAll?.filter(
    (msg) =>
      (msg.sender === activeCustomer && msg.recipient === "Admin") ||
      (msg.sender === "Admin" && msg.recipient === activeCustomer)
  );

  const handleSend = async () => {
    if (!text.trim() || !activeCustomer) return;
    await sendMessage({
      variables: {
        sender: "Admin",
        recipient: activeCustomer,
        text,
      },
    });
    setText("");
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleClear = async () => {
    if (!activeCustomer) return;
    await clearMessages({ variables: { sender: activeCustomer } });
    setActiveCustomer(null);

    setLastReadMap((prev) => {
      const updated = { ...prev };
      delete updated[activeCustomer];
      localStorage.setItem("lastReadMap", JSON.stringify(updated));
      return updated;
    });
  };

  const handleClearAll = async () => {
    const confirmClear = window.confirm(
      "ທ່ານຕ້ອງການລົບຂໍ້ຄວາມລູກຄ້າທັງໝົດ ຫຼືບໍ?"
    );
    if (!confirmClear) return;

    await clearMessages({ variables: { sender: null } });

    setActiveCustomer(null);

    localStorage.removeItem("lastReadMap");
    setLastReadMap({});
  };

  const handleSelectCustomer = (customer) => {
    setActiveCustomer(customer);
    setLastReadMap((prev) => {
      const updated = {
        ...prev,
        [customer]: Date.now(),
      };
      localStorage.setItem("lastReadMap", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row">
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <h4 style={{ fontWeight: "bold" }}>ແຊັດກັບລູກຄ້າ </h4>
        </div>
        <div
          className="container-fluid d-flex border rounded shadow"
          style={{ width: "100%", height: "70vh" }}
        >
          {/* Sidebar */}
          <div className="col-3 border-end p-2 overflow-auto">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">👥 ລູກຄ້າທີ່ແຊັດມາ</h5>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleClearAll}
              >
                ລົບທັງໝົດ
              </button>
            </div>
            <hr />

            {uniqueCustomers.length === 0 && (
              <p className="text-muted">ຍັງບໍ່ມີລູກຄ້າ</p>
            )}

            {uniqueCustomers.map((customer) => {
              const customerMsgs = customerMap[customer];
              const lastMsg = customerMsgs[customerMsgs.length - 1];
              const isActive = customer === activeCustomer;

              const lastRead = lastReadMap[customer] || 0;
              const lastMsgTime = new Date(lastMsg?.createdAt).getTime();
              const hasNewMessage =
                lastMsg?.sender !== "Admin" && lastMsgTime > lastRead;

              const isNewCustomer = !lastRead;

              return (
                <div
                  key={customer}
                  onClick={() => handleSelectCustomer(customer)}
                  className={`customer-item ${isActive ? "active" : ""}`}
                  style={{
                    position: "relative",
                    borderRadius: "8px",
                    border: isActive ? "2px solid #dc3545" : "1px solid #ddd",
                    background: isActive ? "#dc3545" : "#fff",
                    color: isActive ? "#fff" : "#000",
                    marginBottom: "8px",
                    padding: "12px",
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div className="fw-bold mb-1">ຊື້: {customer}</div>

                  <div
                    className="small text-truncate"
                    style={{
                      fontSize: "0.9rem",
                      opacity: isActive ? 0.9 : 0.7,
                    }}
                  >
                    {lastMsg?.text || "ບໍ່ມີຂໍ້ຄວາມ"}
                  </div>

                  <div
                    className="small"
                    style={{
                      textAlign: "right",
                      fontSize: "0.8rem",
                      opacity: isActive ? 0.9 : 0.6,
                    }}
                  >
                    {new Date(lastMsg?.createdAt).toLocaleTimeString()}
                  </div>

                  {!isActive &&
                    hasNewMessage &&
                    (isNewCustomer ? (
                      <span
                        style={{
                          position: "absolute",
                          top: "12px",
                          right: "12px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "red",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          position: "absolute",
                          top: "12px",
                          right: "12px",
                          backgroundColor: "#fff",
                          color: "red",
                          fontSize: "0.7rem",
                          padding: "2px 6px",
                          borderRadius: "10px",
                          border: "1px solid red",
                          fontWeight: "bold",
                        }}
                      >
                        ມີຂໍ້ຄວາມໃໝ່
                      </span>
                    ))}
                </div>
              );
            })}
          </div>

          {/* Chat Area */}
          <div className="col d-flex flex-column p-3">
            {!activeCustomer && (
              <div className="text-muted text-center my-auto">
                👉 ກະລຸນາເລືອກລູກຄ້າດ້ານຊ້າຍ
              </div>
            )}

            {activeCustomer && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">{activeCustomer}</h5>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleClear}
                    disabled={clearing}
                  >
                    ລົບຂໍ້ຄວາມ ແລະ ລູກຄ້າຜູ້ນີ້
                  </button>
                </div>
                <div className="flex-grow-1 border rounded p-3 mb-3 overflow-auto">
                  {loading && <p>Loading...</p>}
                  {error && <p className="text-danger">{error.message}</p>}

                  {messages?.map((msg) => (
                    <div
                      key={msg._id}
                      className={`p-2 mb-2 rounded ${msg.sender === "Admin"
                        ? "bg-danger text-black ms-auto"
                        : "bg-light text-while border"
                        }`}
                      style={{ maxWidth: "70%" }}
                    >
                      <div className="small fw-bold">{msg.sender}</div>
                      <hr className="my-1" />
                      <div>{msg.text}</div>
                      <div className="small text-end text-white">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}

                  <div ref={messagesEndRef} />
                </div>

                <div className="d-flex gap-2">
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="ພິມຂໍ້ຄວາມ..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  ></textarea>
                  <button
                    className="btn btn-danger"
                    disabled={sending || !text.trim()}
                    onClick={handleSend}
                  >
                    <Send size={20} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
